import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-log-viewer>` says about itself.
 *
 * Reading a log is a STRUCTURED read. Timestamp, event and detail as three columns, with a filter
 * down to the lines that matter, is the difference between a log you can use and a log you scroll —
 * the single most useful thing the reference tool does that the incumbent console does not, and the
 * reason this is a component rather than a `<pre>`.
 *
 * Two decisions worth knowing. The line number is the ORIGINAL one: a filtered log renumbered 1..40
 * destroys the only thing connecting a line back to the raw text or to somebody else's screenshot.
 * And a log that could not be parsed gets shown as raw text WITH a line saying so — never a blank
 * panel that reads as a log with nothing in it.
 */
const docs: ComponentDocs = {
  aliases: ['log reader', 'debug log', 'log detail', 'structured log', 'log lines'],

  answers: [
    'read one log without scrolling a wall of text',
    'see only the debug lines',
    'find every line mentioning a record id',
    'find the errors in a long log',
  ],

  placements: ['appPage'],

  whenToUse: 'When one log has been chosen and the question is what happened in it.',

  insteadUse: [
    { name: 'LogList', when: 'somebody is choosing WHICH log to read.' },
    {
      name: 'FieldRenderer',
      when: 'the text is a record’s rich-text field being displayed rather than machine output being searched.',
    },
  ],

  inputs: [
    {
      name: 'lines',
      type: 'json',
      description:
        'The log as structured lines: `{ number, at?, event?, detail?, category? }[]`. `number` is the ' +
        'position in the ORIGINAL log and is never renumbered by filtering.',
      default: [],
    },
    {
      name: 'raw',
      type: 'text',
      description:
        'The log as raw text, for a log that could not be parsed. Shown ONLY when there are no lines, ' +
        'together with a line saying it is unstructured — so unparseable and empty never look alike.',
    },
    {
      name: 'category',
      attribute: 'category',
      type: 'text',
      options: ['executable', 'debug', 'system', 'error'],
      description:
        'Show only lines of this category. A FILTER, not a text search for the category name — ' +
        'searching for "debug" also matches every line whose detail contains the word, and misses ' +
        'debug lines that do not say so. The dropdown offers only the categories present in this log.',
      fallback: 'every category, when the attribute is absent or empty',
    },
    {
      name: 'search',
      attribute: 'search',
      type: 'text',
      description:
        'Narrows by text across the timestamp, event and detail. Intersects with the category filter ' +
        'rather than replacing it — "the debug lines mentioning this record id" is the actual question.',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say for a log with no lines in it at all.',
      default: 'This log is empty.',
    },
  ],

  events: [
    { name: 'caos-log-filter', when: 'The category filter changes.', carries: '`{ category, search }`.' },
  ],

  state: [
    { name: 'visibleLines', type: 'CaosLogLine[]', description: 'The lines surviving both filters.' },
    {
      name: 'categories',
      type: 'CaosLogCategory[]',
      description: 'The categories present in this log — what the filter offers.',
    },
  ],

  parts: [
    { part: 'viewer', is: 'The outer column.' },
    { part: 'toolbar', is: 'The filter and search row.' },
    { part: 'filter', is: 'The category dropdown.' },
    { part: 'search', is: 'The search box.' },
    { part: 'summary', is: 'The line count and error count.' },
    { part: 'lines', is: 'The scrolling list of lines.' },
    { part: 'line', is: 'One line.' },
    { part: 'at', is: 'A line’s timestamp.' },
    { part: 'event', is: 'A line’s event name.' },
    { part: 'detail', is: 'The rest of a line.' },
    { part: 'raw', is: 'The unparsed text.', when: 'The log arrived as raw text with no lines.' },
  ],

  accessibility:
    'The summary is a `role="status"` live region stating how many of the whole log survived the ' +
    'filters — a filtered log that does not say it is filtered is how somebody concludes a run ' +
    'logged four lines. The error count is stated there in words rather than being left to the red ' +
    'on the rows. Both controls carry their own accessible names. The original line numbers are ' +
    'kept, so a line read here can be found in the raw text a colleague is looking at.',

  examples: [
    {
      key: 'structured',
      label: 'A log read as lines',
      summary:
        'Three columns and the original line numbers. The error count is stated in the summary, not ' +
        'left to somebody spotting the red.',
      placement: {
        component: 'caos-log-viewer',
        inputs: {
          lines: [
            { number: 1, at: '15:58:01.004', event: 'EXECUTION_STARTED', category: 'system' },
            { number: 2, at: '15:58:01.006', event: 'CODE_UNIT_STARTED', detail: 'AccountTrigger on Account', category: 'executable' },
            { number: 3, at: '15:58:01.021', event: 'USER_DEBUG', detail: 'resolving 412 line items', category: 'debug' },
            { number: 4, at: '15:58:01.244', event: 'SOQL_EXECUTE_BEGIN', detail: 'select id from account where id = :ids', category: 'executable' },
            { number: 5, at: '15:58:02.118', event: 'FATAL_ERROR', detail: 'System.NullPointerException line 48', category: 'error' },
            { number: 6, at: '15:58:02.119', event: 'EXECUTION_FINISHED', category: 'system' },
          ],
        },
      },
      stage: { needsWidth: 860 },
    },
    {
      key: 'filtered',
      label: 'Filtered to the debug lines',
      summary:
        'The numbers stay as they were. A filtered log renumbered 1..40 destroys the only thing ' +
        'connecting a line back to the raw text.',
      placement: {
        component: 'caos-log-viewer',
        inputs: {
          category: 'debug',
          lines: [
            { number: 1, at: '15:58:01.004', event: 'EXECUTION_STARTED', category: 'system' },
            { number: 3, at: '15:58:01.021', event: 'USER_DEBUG', detail: 'resolving 412 line items', category: 'debug' },
            { number: 9, at: '15:58:01.880', event: 'USER_DEBUG', detail: 'tax band B applied', category: 'debug' },
            { number: 11, at: '15:58:02.118', event: 'FATAL_ERROR', detail: 'System.NullPointerException', category: 'error' },
          ],
        },
      },
      stage: { needsWidth: 860 },
    },
    {
      key: 'unparsed',
      label: 'A log that could not be read as lines',
      summary:
        'The text is shown as it arrived, with a line saying so — so "could not be parsed" and ' +
        '"nothing happened" never look the same.',
      placement: {
        component: 'caos-log-viewer',
        inputs: {
          raw: '62.0 APEX_CODE,DEBUG;APEX_PROFILING,INFO\nExecute Anonymous: System.debug(1);\n15:58:01.4 (4123456)|USER_DEBUG|[1]|DEBUG|1',
        },
      },
      stage: { needsWidth: 860 },
    },
  ],
};

export default docs;
